import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS, ROUTE_IDS, } from '../routes/ids.js';
import { MAINTENANCE_DELEGATION_CAPABILITIES, MAINTENANCE_DELEGATION_PURPOSES, } from '../../access/maintenance-delegation.js';
const tk = (value) => value;
const telemetry = (value) => value;
const queryId = (value) => value;
const commandId = (value) => value;
const fieldId = (value) => value;
const formId = (value) => value;
const ruleId = (value) => value;
const module = MODULE_KEYS.ADMIN;
const feature = FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS;
function column(id, overrides = {}) {
    return {
        id,
        schemaVersion: 1,
        module,
        feature,
        telemetryId: telemetry(`experience.table.maintenanceDelegations.${id}`),
        accessibilityLabelKey: tk(`a11y.col.maintenanceDelegations.${id}`),
        privacyClassification: 'INTERNAL',
        availability: 'AVAILABLE',
        owner: 'platform-security',
        field: id,
        labelKey: tk(`maintenanceDelegations.columns.${id}`),
        fieldType: 'text',
        sortable: false,
        filterable: false,
        searchable: false,
        groupable: false,
        hideable: true,
        defaultVisible: true,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: 180,
        minWidth: 100,
        maxWidth: 420,
        align: 'left',
        nullDisplayBehavior: 'dash',
        ...overrides,
    };
}
const revokeAction = {
    id: 'action.platformSupportDelegation.revoke',
    schemaVersion: 1,
    module,
    feature,
    telemetryId: telemetry('experience.action.platformSupportDelegation.revoke'),
    accessibilityLabelKey: tk('a11y.action.platformSupportDelegation.revoke'),
    privacyClassification: 'INTERNAL',
    availability: 'AVAILABLE',
    owner: 'platform-security',
    operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE,
    labelKey: tk('maintenanceDelegations.actions.revoke'),
    iconKey: 'delete',
    kind: 'navigate',
    intent: 'danger',
    idempotencyRequired: false,
    successBehavior: 'NAVIGATE_TO',
    targetRouteId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE,
    successNavigateTo: '/platform/support-delegations/:delegationId/revoke',
    errorBehavior: 'SHOW_INLINE_ERROR',
    order: 20,
    group: 'danger',
};
export const MAINTENANCE_DELEGATION_TABLE = {
    id: 'table.admin.platformSupportDelegations',
    schemaVersion: 1,
    module,
    feature,
    telemetryId: telemetry('experience.table.platformSupportDelegations'),
    accessibilityLabelKey: tk('a11y.table.platformSupportDelegations'),
    privacyClassification: 'INTERNAL',
    availability: 'AVAILABLE',
    owner: 'platform-security',
    operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
    titleKey: tk('maintenanceDelegations.title'),
    descriptionKey: tk('maintenanceDelegations.description'),
    dataSourceId: queryId('query.admin.platform-support-delegations.list'),
    rowIdField: 'id',
    columns: [
        column('supportUserIdentityId', { searchable: true, requiredVisible: true, defaultWidth: 240 }),
        column('targetOrganizationId', { searchable: true, requiredVisible: true, defaultWidth: 240 }),
        column('purpose', { filterable: true, fieldType: 'select', defaultWidth: 230 }),
        column('ticketReference', { searchable: true, defaultWidth: 180 }),
        column('status', { filterable: true, fieldType: 'status', requiredVisible: true, defaultWidth: 130 }),
        column('effectiveFrom', { fieldType: 'datetime', defaultWidth: 190 }),
        column('expiresAt', {
            fieldType: 'datetime',
            sortable: true,
            requiredVisible: true,
            defaultWidth: 190,
        }),
        column('sessionStartedAt', { fieldType: 'datetime', defaultVisible: false, defaultWidth: 190 }),
        column('sessionEndedAt', { fieldType: 'datetime', defaultVisible: false, defaultWidth: 190 }),
        column('policyVersion', { defaultVisible: false, defaultWidth: 220 }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'expiresAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: {
        sorting: true,
        filtering: true,
        globalSearch: true,
        grouping: false,
        aggregation: false,
        pagination: true,
        selection: false,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        columnPinning: false,
        savedViews: false,
        export: false,
        refresh: true,
        density: true,
        create: true,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: false,
        showCreateAction: true,
    },
    rowActions: [revokeAction],
};
function field(id, name, fieldType = 'text', required = false, overrides = {}) {
    return {
        id: fieldId(id),
        name,
        labelKey: tk(`maintenanceDelegations.fields.${name}`),
        helpTextKey: tk(`maintenanceDelegations.help.${name}`),
        fieldType,
        layout: { md: 12 },
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        validation: required
            ? { rules: [{ ruleId: ruleId('required'), messageKey: tk('validation.required') }], validateWhen: 'submit' }
            : undefined,
        ...overrides,
    };
}
function action(id, label, operationId, command, intent) {
    return {
        id,
        schemaVersion: 1,
        module,
        feature,
        telemetryId: telemetry(`experience.${id}`),
        accessibilityLabelKey: tk(`a11y.${id}`),
        privacyClassification: 'INTERNAL',
        availability: 'AVAILABLE',
        owner: 'platform-security',
        operationId: operationId,
        labelKey: tk(label),
        kind: 'command',
        submitBehavior: 'form_submit',
        submitConfig: { validatesForm: true, requiresDirty: true, requiresValid: true, resetBehavior: 'none' },
        commandId: commandId(command),
        intent,
        idempotencyRequired: true,
        successBehavior: 'NAVIGATE_BACK',
        successMessageKey: tk('common.saveSuccess'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        order: 20,
        group: 'primary',
    };
}
const createFields = [
    field('field.maintenanceDelegation.supportUserIdentityId', 'supportUserIdentityId', 'text', true),
    field('field.maintenanceDelegation.targetOrganizationId', 'targetOrganizationId', 'text', true),
    field('field.maintenanceDelegation.targetBranchId', 'targetBranchId'),
    field('field.maintenanceDelegation.targetClientAccountId', 'targetClientAccountId'),
    field('field.maintenanceDelegation.targetPortfolioAccountId', 'targetPortfolioAccountId'),
    field('field.maintenanceDelegation.purpose', 'purpose', 'select', true, {
        options: MAINTENANCE_DELEGATION_PURPOSES.map((value) => ({ value, labelKey: tk(`maintenanceDelegations.purpose.${value}`) })),
    }),
    field('field.maintenanceDelegation.justification', 'justification', 'textarea', true),
    field('field.maintenanceDelegation.ticketReference', 'ticketReference', 'text', true),
    field('field.maintenanceDelegation.capabilities', 'capabilities', 'multiselect', true, {
        options: MAINTENANCE_DELEGATION_CAPABILITIES.map((value) => ({ value, labelKey: tk(`maintenanceDelegations.capability.${value}`) })),
    }),
    field('field.maintenanceDelegation.allowedOperationIds', 'allowedOperationIds', 'multiselect', true, {
        optionSource: {
            adapterId: 'experience.options.delegableOperations',
            operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_CREATE,
            valuePath: 'id',
            labelPath: 'id',
            allowSearch: true,
            minimumSearchLength: 0,
        },
    }),
    field('field.maintenanceDelegation.resourceIds', 'resourceIds', 'multiselect'),
    field('field.maintenanceDelegation.effectiveFrom', 'effectiveFrom', 'datetime', true),
    field('field.maintenanceDelegation.expiresAt', 'expiresAt', 'datetime', true),
];
export const MAINTENANCE_DELEGATION_CREATE_FORM = {
    id: formId('form.admin.platformSupportDelegation.create'),
    schemaVersion: 1,
    module,
    feature,
    telemetryId: telemetry('experience.form.platformSupportDelegation.create'),
    accessibilityLabelKey: tk('a11y.form.platformSupportDelegation.create'),
    privacyClassification: 'INTERNAL',
    availability: 'AVAILABLE',
    owner: 'platform-security',
    titleKey: tk('maintenanceDelegations.createTitle'),
    descriptionKey: tk('maintenanceDelegations.createDescription'),
    mode: 'create',
    operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_CREATE,
    fields: createFields,
    actions: [
        action('action.platformSupportDelegation.create', 'maintenanceDelegations.actions.create', OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_CREATE, 'cmd.admin.platform-support-delegations.create', 'primary'),
    ],
    submission: {
        defaultCommandId: commandId('cmd.admin.platform-support-delegations.create'),
        includeFields: createFields.map((item) => item.id),
    },
};
export const MAINTENANCE_DELEGATION_REVOKE_FORM = {
    id: formId('form.admin.platformSupportDelegation.revoke'),
    schemaVersion: 1,
    module,
    feature,
    telemetryId: telemetry('experience.form.platformSupportDelegation.revoke'),
    accessibilityLabelKey: tk('a11y.form.platformSupportDelegation.revoke'),
    privacyClassification: 'INTERNAL',
    availability: 'AVAILABLE',
    owner: 'platform-security',
    titleKey: tk('maintenanceDelegations.revokeTitle'),
    descriptionKey: tk('maintenanceDelegations.revokeDescription'),
    mode: 'edit',
    operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE,
    fields: [field('field.maintenanceDelegation.revokeReason', 'reason', 'textarea', true)],
    actions: [
        action('action.platformSupportDelegation.confirmRevoke', 'maintenanceDelegations.actions.confirmRevoke', OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE, 'cmd.admin.platform-support-delegations.revoke', 'danger'),
    ],
    submission: {
        defaultCommandId: commandId('cmd.admin.platform-support-delegations.revoke'),
        includeFields: [fieldId('field.maintenanceDelegation.revokeReason')],
    },
};
